/* eslint-disable prettier/prettier */
import { useEffect, useState } from 'react';
import { Button } from '../../components/ui/Button';
import { useNavigate } from 'react-router-dom';
import { FileService } from '../../services/file.service';

type Group = {
  groupName: string;
  treaties: { id: string; year: number; name: string }[];
};

type Selection = Record<string, { left: string; right: string }>;

export const ComparePYPage = () => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [selected, setSelected] = useState<Selection>({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const res = await FileService.getComparePYGroups();
        if (Array.isArray(res.data)) {
          setGroups(res.data);
          const initial = Object.fromEntries(
            res.data.map((g) => [
              g.groupName,
              {
                left: g.treaties[0]?.id || '',
                right: g.treaties[1]?.id || '',
              },
            ]),
          );
          setSelected(initial);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchGroups();
  }, []);

  const handleChange = (groupName: string, side: 'left' | 'right', value: string) => {
    setSelected((prev) => ({
      ...prev,
      [groupName]: {
        ...prev[groupName],
        [side]: value,
      },
    }));
  };

  const compare = (groupName: string) => {
    const sel = selected[groupName];
    const group = groups.find((g) => g.groupName === groupName);
    if (!sel?.left || !sel?.right || !group) return;

    const leftIndex = group.treaties.findIndex((t) => t.id === sel.left);
    const rightIndex = group.treaties.findIndex((t) => t.id === sel.right);
    const latestId = group.treaties.at(-1)?.id;

    if (leftIndex === -1 || rightIndex === -1 || !latestId || sel.left === sel.right) return;

    const leftLabel = `v${leftIndex + 1}`;
    const rightLabel = `v${rightIndex + 1}`;

    navigate(`${latestId}?left=${sel.left}&right=${sel.right}`);
  };

  return (
    <div>
      <h1 className="pb-4 text-4xl font-bold text-center sm:pt-0 sm:pb-7 sm:text-left text-primary">
        <span className="inline sm:hidden">Compare PY</span>
        <span className="hidden sm:inline">Compare Treaty Versions Per Year</span>
      </h1>

      {loading ? (
        <p className="mt-6 text-xl text-center text-primary">Loading...</p>
      ) : groups.length === 0 ? (
        <p className="mt-6 text-xl text-center text-primary text-muted-foreground">
          No versions found.
        </p>
      ) : (
        <table className="sm:w-[90%] text-left bg-white border border-gray-200 rounded-lg shadow-sm text-sm sm:text-lg">
          <thead>
            <tr className="px-4 py-3 font-semibold border text-md bg-gray-50 text-muted-foreground">
              <th className="p-2 sm:p-3 w-[40%]">Treaty</th>
              <th className="p-2 sm:p-3 w-[20%]">Version 1</th>
              <th className="p-2 sm:p-3 w-[20%]">Version 2</th>
              <th className="p-2 sm:p-3 w-[20%]" />
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <tr key={group.groupName} className="border-t">
                <td className="p-2">{group.groupName}</td>
                <td className="p-2">
                  <select
                    value={selected[group.groupName]?.left || ''}
                    onChange={(e) => handleChange(group.groupName, 'left', e.target.value)}
                    className="px-2 py-1 border border-gray-300 rounded"
                  >
                    {group.treaties
                      .filter((t) => t.id !== selected[group.groupName]?.right) 
                      .map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.year}
                        </option>
                      ))}
                  </select>
                </td>

                <td className="p-2">
                  <select
                    value={selected[group.groupName]?.right || ''}
                    onChange={(e) => handleChange(group.groupName, 'right', e.target.value)}
                    className="px-2 py-1 border border-gray-300 rounded"
                  >
                    {group.treaties
                      .filter((t) => t.id !== selected[group.groupName]?.left)
                      .map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.year}
                        </option>
                      ))}
                  </select>
                </td>

                <td className="p-2 text-center">
                  <Button
                    onClick={() => compare(group.groupName)}
                    size="sm"
                    className="w-full sm:w-auto"
                  >
                    Compare
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};
